/**
 * `/alert-rules` — Task U16 (Tier-3 live-tenant readiness), plan unit U16 and KTD13.
 *
 * `workforce.manage_alerts` has existed since WP-L1 and until this screen it gated
 * nothing anybody could reach from a browser: the three endpoints were live, the
 * permission was in the matrix, and the only way a company could change what its
 * people are alerted about was a database client or a `curl`.
 *
 * ── The lifecycle is not this screen's ──────────────────────────────────────
 *
 * `useVersionedConfig` (U14, KTD13) owns list → create → patch, the touched-fields
 * draft, the compare-and-set and the Skeleton / Empty / Retry / Conflict states, and
 * `VersionedConfigPanel` owns what each of them looks like. What is here is this
 * configuration's own words, its own fields, and the two things alert rules have that
 * no other configuration screen does: a **catalogue of many rules**, each with its own
 * version lineage, and **automation actions**.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THE ACTIVATE BUTTON, AND WHY THERE ISN'T ONE
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * The plan told U16 to check what activation takes before trusting the button, after
 * U15's Activate produced a policy that governed no machine. The answer here is not
 * "it takes a flag the hook cannot send" but something blunter: **there is no activate
 * endpoint at all**. `/alert-rules` publishes `GET`, `POST` and `PATCH /{id}` and
 * nothing else. Putting a rule into force is `PATCH /{id}` with `{"status":"active"}`
 * and an `If-Match` carrying the version — and the body is load-bearing twice over:
 * `AlertRulePatchRequest` is `minProperties: 1`, so an empty one is a `422`; and
 * `patchAlertRule` branches on the patch being **status-only** to move the row instead
 * of minting a version, so a body carrying anything else forks the lineage as well.
 *
 * U14's `activate` sends `bodyWith({}, precondition)` — `{}` on an `if-match` surface.
 * So this screen calls `state.activate` **nowhere**, and its `activation.path` is
 * `alertRuleActivatePath`, which throws rather than returning a URL: a control wired to
 * it would fail loudly instead of sending the one body the server refuses. `status` is
 * a field of the edit form, and the same Save every other edit uses sends the status
 * move. Whatever the form sends, `alert-rules.test.tsx` sends: no helper there supplies
 * a field this screen does not.
 *
 * ── One hook, two lists, because the hook models versions of ONE thing ──────
 *
 * `useVersionedConfig` requires `isActive` to pick exactly one row, and its `diff`
 * compares the draft against that row. A company's alert rules are **N rules, each
 * with a lineage**, and `wp_alert_rules_active_uk` permits one `active` row per
 * `(company_id, rule_key)` — so a catalogue has as many active rows as it has rules.
 * Rather than lie about that, the screen moves the hook's own list:
 *
 *   catalogue / create   `listPath = /alert-rules`      every rule, every version.
 *                        The draft is empty in both (the create form keeps its own
 *                        state, below), so the diff has nothing to compare and the
 *                        several active badges are simply true.
 *   editing rule K       `listPath = /alert-rules?rule_key=K`   one lineage, in which
 *                        **at most** one row is active — so the hook's promise holds,
 *                        and where there is one, the diff's baseline is the version
 *                        really in force for K.
 *
 * "At most", not "exactly", and fix round 1 corrected that: a rule that has only ever
 * been a draft, or one switched off, has **no** active row. `activeRow` is then `null`
 * and the panel's diff — always headed "against the version in force" — renders every
 * touched key through `config.diff.absent`, a sentence written for a field the active
 * version does not carry rather than for there being no active version. The screen says
 * that itself (`u16-no-active`) instead of letting the hook's wording stand for it.
 *
 * ── The create form does not use the hook's draft, on purpose ───────────────
 *
 * U15 found that one `draft` serves two write intents. On this backend that is not a
 * shared name but a type error waiting to happen: `trigger` and `rule_key` are in
 * `AlertRuleCreateRequest` and **absent** from `AlertRulePatchRequest`, which is
 * `additionalProperties: false`, and the two `status` enums differ (`draft|active`
 * against `active|disabled`). A single draft holding a trigger typed on the create form
 * would put it into the next `PATCH` body and earn a `422` naming a field the person
 * never touched. So create keeps its own state and `createBody` reads it; the hook's
 * draft is the **edit** draft alone, which is what makes "a patch carries only what was
 * touched" true here.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';
import {
  ALERT_ACTION_PARAMETERS,
  ALERT_ACTION_PERFORMED,
  ALERT_ACTIONS,
  ALERT_GROUP_BY_FIELDS,
  ALERT_RULES_PATH,
  ALERT_SEVERITIES,
  ALERT_TRIGGER_FIELDS,
  ALERT_TRIGGERS,
  CREATABLE_STATUSES,
  EQUALITY_OPERATORS,
  OFFERED_OPERATORS,
  PATCHABLE_STATUSES,
  alertRuleActivatePath,
  alertRuleKeyPath,
  alertRulePath,
  type AlertAction,
  type AlertGroupByField,
  type AlertRule,
  type AlertRuleCondition,
  type AlertRuleCreateRequest,
  type AlertRuleDraft,
  type AlertRuleListResponse,
  type AlertSeverity,
  type AlertTrigger,
  type ConditionField,
  type CreatableStatus,
  type PatchableStatus,
} from '../../api/alertRules';
import { VersionedConfigPanel } from '../../config/VersionedConfigPanel';
import { useVersionedConfig } from '../../config/useVersionedConfig';
import { ScreenHeading } from '../../shell/ScreenHeading';

/** Which list the hook is reading, and which form is on screen. */
type Mode =
  | { readonly kind: 'catalogue' }
  | { readonly kind: 'create' }
  | { readonly kind: 'edit'; readonly ruleKey: string };

/** The create form's own state — deliberately not the hook's draft. See the header. */
interface NewRule {
  readonly name: string;
  readonly trigger: AlertTrigger;
  readonly conditions: readonly AlertRuleCondition[];
  readonly severity: AlertSeverity;
  readonly actions: readonly AlertAction[];
  readonly window_minutes: number;
  readonly dedup_window_minutes: number;
  readonly cooldown_minutes: number;
  readonly group_by: readonly AlertGroupByField[];
  readonly status: CreatableStatus;
}

/**
 * The defaults, and they are the **server's** own, transcribed from
 * `AlertRuleCreateRequest`: `window_minutes` 60, `dedup_window_minutes` 60,
 * `cooldown_minutes` 0, `group_by` `['employee_id']`.
 *
 * `status` is the one deliberate departure. The contract defaults it to `active`; this
 * form defaults it to `draft` and sends it explicitly, because a rule that starts
 * firing at named people the moment somebody presses a button for the first time is not
 * a default a monitoring product should choose on the administrator's behalf. Both
 * values are offered.
 */
function defaultNewRule(): NewRule {
  return {
    name: '',
    trigger: 'agent_offline',
    conditions: [],
    severity: 'medium',
    actions: [],
    window_minutes: 60,
    dedup_window_minutes: 60,
    cooldown_minutes: 0,
    group_by: ['employee_id'],
    status: 'draft',
  };
}

const SEVERITY_LABEL: Readonly<Record<AlertSeverity, MessageKey>> = {
  informational: 'alertRules.severity.informational',
  low: 'alertRules.severity.low',
  medium: 'alertRules.severity.medium',
  high: 'alertRules.severity.high',
  critical: 'alertRules.severity.critical',
};

const ACTION_LABEL: Readonly<Record<AlertAction, MessageKey>> = {
  notify_manager: 'alertRules.action.notify_manager',
  notify_it: 'alertRules.action.notify_it',
  notify_hr: 'alertRules.action.notify_hr',
  create_incident: 'alertRules.action.create_incident',
  create_achau_task: 'alertRules.action.create_achau_task',
};

/** What each action actually does when it fires, in this build. Per type, not shared. */
const ACTION_NOTE: Readonly<Record<AlertAction, MessageKey>> = {
  notify_manager: 'alertRules.action.note.notify_manager',
  notify_it: 'alertRules.action.note.notify_it',
  notify_hr: 'alertRules.action.note.notify_hr',
  create_incident: 'alertRules.action.note.create_incident',
  create_achau_task: 'alertRules.action.note.create_achau_task',
};

/** The comparand a newly picked field starts with, by kind. */
function defaultValueFor(field: ConditionField): number | string | boolean {
  if (field.kind === 'number') return 0;
  if (field.kind === 'boolean') return true;
  return field.options[0] ?? '';
}

/** The operators a field of this kind may be compared with. */
function operatorsFor(field: ConditionField): readonly string[] {
  return field.kind === 'number' ? OFFERED_OPERATORS : EQUALITY_OPERATORS;
}

function conditionFor(field: ConditionField): AlertRuleCondition {
  return {
    field: field.name,
    operator: operatorsFor(field)[0] ?? '==',
    value: defaultValueFor(field),
  };
}

/** `offline_minutes >= 1440` — one rule's conditions in words, for the table. */
function describeConditions(conditions: readonly AlertRuleCondition[]): string {
  return conditions.map((one) => `${one.field} ${one.operator} ${String(one.value)}`).join(' AND ');
}

/* ───────────────────────────────────────────────────────── the condition editor */

interface ConditionEditorProps {
  readonly conditions: readonly AlertRuleCondition[];
  readonly fields: readonly ConditionField[];
  readonly onChange: (next: readonly AlertRuleCondition[]) => void;
  readonly testId: string;
}

/**
 * Conditions, built only out of the fields the trigger's fact source publishes.
 *
 * **There is no text box for a field name anywhere in this component**, and that is the
 * R18 guardrail rather than a styling choice: `conditions[].field` is a free lower-case
 * identifier on the wire, so a text box here is all it would take to write
 * `productivity_score >= 85` — an activity measurement turned into a target somebody is
 * judged against. The vocabulary is `ALERT_TRIGGER_FIELDS`, which contains no score, no
 * quota and no target, and a test asserts that over every entry.
 */
function ConditionEditor(props: ConditionEditorProps): ReactNode {
  const { t } = useI18n();
  const { conditions, fields, onChange, testId } = props;

  const replace = (index: number, next: AlertRuleCondition): void => {
    onChange(conditions.map((one, at) => (at === index ? next : one)));
  };

  if (fields.length === 0) {
    // Not a disabled control: this trigger has no fact source in this build, so there
    // is no field to compare and nothing a condition could say.
    return (
      <p className="wp-small wp-muted" data-testid={`${testId}-nofields`}>
        {t('alertRules.condition.noFields')}
      </p>
    );
  }

  return (
    <div className="wp-c-conditions" data-testid={testId}>
      {/* A group key, which is where uppercase is allowed to live (DESIGN.md §12,
          Luật Nhãn In Hoa) — not a 12px heading pretending to be one. */}
      <h3 className="wp-label">{t('alertRules.condition.title')}</h3>
      <p className="wp-small wp-muted">{t('alertRules.condition.hint')}</p>
      {conditions.map((condition, index) => {
        const field = fields.find((one) => one.name === condition.field) ?? fields[0];
        if (field === undefined) return null;
        const rowId = `${testId}-${String(index)}`;
        return (
          <div className="wp-c-cond" key={rowId} data-testid={rowId}>
            <div className="wp-field">
              <label className="wp-field__label" htmlFor={`${rowId}-field`}>
                {t('alertRules.condition.field')}
              </label>
              <select
                className="wp-field__input"
                id={`${rowId}-field`}
                data-testid={`${rowId}-field`}
                value={condition.field}
                onChange={(event) => {
                  const picked = fields.find((one) => one.name === event.target.value);
                  // A field of another kind takes another operator and another
                  // comparand: `never_reported > 0` is refused by the server, so the
                  // row is rebuilt rather than half-changed.
                  if (picked !== undefined) replace(index, conditionFor(picked));
                }}
              >
                {fields.map((one) => (
                  <option key={one.name} value={one.name}>
                    {one.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="wp-field">
              <label className="wp-field__label" htmlFor={`${rowId}-operator`}>
                {t('alertRules.condition.operator')}
              </label>
              <select
                className="wp-field__input"
                id={`${rowId}-operator`}
                data-testid={`${rowId}-operator`}
                value={condition.operator}
                onChange={(event) => {
                  replace(index, { ...condition, operator: event.target.value });
                }}
              >
                {operatorsFor(field).map((operator) => (
                  <option key={operator} value={operator}>
                    {operator}
                  </option>
                ))}
              </select>
            </div>

            <div className="wp-field">
              <label className="wp-field__label" htmlFor={`${rowId}-value`}>
                {t('alertRules.condition.value')}
              </label>
              {field.kind === 'number' ? (
                <input
                  className="wp-field__input"
                  id={`${rowId}-value`}
                  data-testid={`${rowId}-value`}
                  type="number"
                  value={typeof condition.value === 'number' ? condition.value : 0}
                  onChange={(event) => {
                    replace(index, { ...condition, value: Number(event.target.value) });
                  }}
                />
              ) : (
                <select
                  className="wp-field__input"
                  id={`${rowId}-value`}
                  data-testid={`${rowId}-value`}
                  value={String(condition.value)}
                  onChange={(event) => {
                    replace(index, {
                      ...condition,
                      value:
                        field.kind === 'boolean'
                          ? event.target.value === 'true'
                          : event.target.value,
                    });
                  }}
                >
                  {(field.kind === 'boolean' ? ['true', 'false'] : field.options).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <button
              type="button"
              className="wp-button"
              data-testid={`${rowId}-remove`}
              onClick={() => {
                onChange(conditions.filter((_one, at) => at !== index));
              }}
            >
              {t('alertRules.condition.remove')}
            </button>
          </div>
        );
      })}
      <button
        type="button"
        className="wp-button"
        data-testid={`${testId}-add`}
        onClick={() => {
          const field = fields[0];
          if (field !== undefined) onChange([...conditions, conditionFor(field)]);
        }}
      >
        {t('alertRules.condition.add')}
      </button>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────── the action editor */

interface ActionEditorProps {
  readonly actions: readonly AlertAction[];
  readonly onChange: (next: readonly AlertAction[]) => void;
  readonly testId: string;
}

/**
 * The automation actions, one checkbox per type from the closed enum, and each picked
 * type's own parameter fields beneath it.
 *
 * Today every type declares zero parameters, because `wp_alert_rules.actions` is a
 * `text[]` of enum keys and there is no parameter channel on the wire at all — see
 * `ALERT_ACTION_PARAMETERS`. The region is rendered **from the registry**, so a type
 * that gains a parameter renders it here without this component changing, and a type
 * with none renders none.
 */
function ActionEditor(props: ActionEditorProps): ReactNode {
  const { t } = useI18n();
  const { actions, onChange, testId } = props;

  return (
    <fieldset className="wp-c-bar__group" data-testid={testId}>
      <legend>{t('alertRules.actions.legend')}</legend>
      <p className="wp-small wp-muted">{t('alertRules.actions.hint')}</p>
      {ALERT_ACTIONS.map((action) => {
        const picked = actions.includes(action);
        const parameters = ALERT_ACTION_PARAMETERS[action];
        return (
          <div key={action}>
            <label className="wp-toggle" htmlFor={`${testId}-${action}`}>
              <input
                id={`${testId}-${action}`}
                type="checkbox"
                data-testid={`${testId}-${action}`}
                checked={picked}
                onChange={(event) => {
                  onChange(
                    event.target.checked
                      ? [...actions, action]
                      : actions.filter((one) => one !== action),
                  );
                }}
              />
              <span>{t(ACTION_LABEL[action])}</span>
            </label>
            {picked ? (
              <>
                <p
                  className="wp-small wp-muted"
                  data-testid={`${testId}-note-${action}`}
                  data-performed={String(ALERT_ACTION_PERFORMED[action])}
                >
                  {t(ACTION_NOTE[action])}
                </p>
                {parameters.length === 0 ? null : (
                  <div data-testid={`${testId}-params-${action}`}>
                    {parameters.map((parameter) => (
                      <div className="wp-field" key={parameter.key}>
                        <label
                          className="wp-field__label"
                          htmlFor={`${testId}-param-${action}-${parameter.key}`}
                        >
                          {parameter.key}
                        </label>
                        {parameter.kind === 'number' ? (
                          <input
                            className="wp-field__input"
                            id={`${testId}-param-${action}-${parameter.key}`}
                            data-testid={`${testId}-param-${action}-${parameter.key}`}
                            type="number"
                            readOnly
                            value={0}
                          />
                        ) : (
                          <select
                            className="wp-field__input"
                            id={`${testId}-param-${action}-${parameter.key}`}
                            data-testid={`${testId}-param-${action}-${parameter.key}`}
                            value={parameter.options[0] ?? ''}
                            onChange={() => {
                              /* A parameter's value has nowhere to go on the wire
                                 yet; the control is here so the day one does, this
                                 is the one place that changes. */
                            }}
                          >
                            {parameter.options.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : null}
          </div>
        );
      })}
    </fieldset>
  );
}

/* ──────────────────────────────────────────────────────────────── the screen */

export function AlertRulesScreen(): ReactNode {
  const { t } = useI18n();
  const [mode, setMode] = useState<Mode>({ kind: 'catalogue' });
  const [newRule, setNewRule] = useState<NewRule>(defaultNewRule);
  /** Which version of the edited lineage the form points at. `null` = the one in force. */
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const state = useVersionedConfig<
    AlertRuleListResponse,
    AlertRule,
    AlertRuleDraft,
    AlertRuleCreateRequest
  >({
    listPath: mode.kind === 'edit' ? alertRuleKeyPath(mode.ruleKey) : ALERT_RULES_PATH,
    versions: (list) => list.data,
    idOf: (rule) => rule.id,
    labelOf: (rule) => `${rule.name} · ${rule.rule_key} · v${String(rule.version)}`,
    // True of every `active` row, because that is what is true: one per `rule_key`,
    // and the catalogue holds many keys. In `edit` the list is one lineage, so the
    // hook's "exactly one" holds and the diff's baseline is the right version.
    isActive: (rule) => rule.status === 'active',
    fieldsOf: (rule) => ({
      // Keys are joined to draft keys by string equality and nothing types that
      // correspondence, so every key here is spelled as `AlertRuleDraft` spells it —
      // and every value is rendered the way the hook's own `renderValue` renders a
      // draft value, so an unchanged field does not read as changed in the diff.
      name: rule.name,
      conditions: JSON.stringify(rule.conditions),
      threshold: rule.threshold === null ? 'null' : String(rule.threshold),
      window_minutes: String(rule.window_minutes),
      group_by: JSON.stringify(rule.group_by),
      severity: rule.severity,
      dedup_window_minutes: String(rule.dedup_window_minutes),
      cooldown_minutes: String(rule.cooldown_minutes),
      actions: JSON.stringify(rule.actions),
      status: rule.status,
      // Rendered but not editable, which `fieldsOf` explicitly allows. `trigger` is
      // absent from `AlertRulePatchRequest` on purpose — a different trigger is a
      // different rule — so it is shown and never offered as an edit.
      trigger: rule.trigger,
    }),
    /*
      The version integer, straight off the list row — `renderAlertRule` puts it there
      and `ifMatchVersion` wants exactly it. Unlike `/policies`, no extra read is
      needed.

      **A retired version returns its token like any other**, and fix round 1 is about
      why. This used to return `null` for one, which the hook reads as "this version's
      token is missing" and the panel renders as `config.token.none` — *"this read
      carried no precondition, so it cannot be edited"*. That sentence was false twice
      over beside every superseded row in the catalogue: the read **did** carry the
      version integer, and the reason the row cannot be edited is that it is retired.
      A policy refusal is not a missing token, and this is the channel for the second.

      Where the refusal belongs is the screen's own `rowAction`, which sees
      `row.fields.status` and returns nothing for a retired row — and the edit form
      refuses a retired target as well, so no write becomes reachable by making the
      token honest.
    */
    preconditionOf: (rule) =>
      Number.isInteger(rule.version) ? { kind: 'if-match', token: String(rule.version) } : null,
    createPath: ALERT_RULES_PATH,
    // The draft argument is deliberately ignored: create has its own state, because
    // create and patch take different field sets on this backend. See the header.
    createBody: () => ({
      name: newRule.name.trim(),
      trigger: newRule.trigger,
      conditions: newRule.conditions,
      severity: newRule.severity,
      actions: newRule.actions,
      window_minutes: newRule.window_minutes,
      dedup_window_minutes: newRule.dedup_window_minutes,
      cooldown_minutes: newRule.cooldown_minutes,
      group_by: newRule.group_by,
      status: newRule.status,
    }),
    patchPath: (rule) => alertRulePath(rule.id),
    // There is no activate endpoint on this backend; activation is a status-only
    // PATCH. This path throws, and nothing calls it — see the header.
    activation: { kind: 'endpoint', method: 'PATCH', path: alertRuleActivatePath },
  });

  const goCatalogue = (): void => {
    setMode({ kind: 'catalogue' });
    setSelectedId(null);
    state.resetDraft();
  };

  /**
   * The version the edit form points at: the chosen one, else the one in force.
   *
   * **A chosen row that has come back `retired` is skipped**, and fix round 1 is about
   * why. A landed content patch retires the incumbent and inserts its replacement, then
   * the hook refetches the lineage (`useVersionedConfig`'s transition-to-`ok` effect) —
   * so the row the person was editing returns under the same id with `status:
   * 'retired'`, `selectedId` still names it, and the form collapsed onto the note
   * saying "this version is history". The ordinary, successful edit ended on what reads
   * as a failure. Falling through to `state.active` lands it on the version their edit
   * actually produced.
   */
  const chosen = state.versions.find((rule) => rule.id === selectedId) ?? null;
  const target: AlertRule | null =
    mode.kind === 'edit'
      ? ((chosen === null || chosen.status === 'retired' ? null : chosen) ??
        state.active ??
        state.versions[0] ??
        null)
      : null;

  /** The value on screen: what was typed, else what the target version holds. */
  const shown = <K extends keyof AlertRuleDraft>(
    key: K,
    fallback: AlertRuleDraft[K],
  ): AlertRuleDraft[K] => state.draft[key] ?? fallback;

  const newRuleFields = ALERT_TRIGGER_FIELDS[newRule.trigger];
  const canCreate =
    newRule.name.trim() !== '' && newRule.conditions.length > 0 && newRule.actions.length > 0;

  const createAction =
    mode.kind === 'create' ? (
      <button
        type="button"
        className="wp-button"
        data-testid="u16-create-cancel"
        onClick={goCatalogue}
      >
        {t('alertRules.create.cancel')}
      </button>
    ) : (
      <button
        type="button"
        className="wp-button wp-button--primary"
        data-testid="u16-create-open"
        onClick={() => {
          setMode({ kind: 'create' });
          setSelectedId(null);
          // The edit draft was typed against a version that is about to leave the
          // screen; carrying it into a create would put somebody else's numbers into
          // a rule they were not looking at.
          state.resetDraft();
          setNewRule(defaultNewRule());
        }}
      >
        {t('alertRules.create.open')}
      </button>
    );

  return (
    <div className="wp-stack">
      <ScreenHeading>{t('alertRules.title')}</ScreenHeading>
      <p className="wp-small wp-muted">{t('alertRules.intro')}</p>
      {/* The guardrail, said on screen and not only in the code: this form builds no
          quota. R18 and KTD2. */}
      <p className="wp-small wp-muted" data-testid="u16-no-quota">
        {t('alertRules.noQuota')}
      </p>

      {mode.kind === 'edit' ? (
        <p className="wp-small">
          <button type="button" className="wp-button" data-testid="u16-back" onClick={goCatalogue}>
            {t('alertRules.back')}
          </button>
        </p>
      ) : null}

      <VersionedConfigPanel
        {...state}
        title={
          mode.kind === 'edit'
            ? t('alertRules.panel.lineage', { key: mode.ruleKey })
            : t('alertRules.panel.title')
        }
        subject={t('alertRules.subject')}
        emptyTitle={t('alertRules.empty.title')}
        emptyDescription={t('alertRules.empty.body')}
        createAction={createAction}
        testId="u16-config"
        rowAction={(row) =>
          /*
            A retired version is history: `patchAlertRule` refuses it with
            `WORKFORCE_INVALID_STATE_TRANSITION`, so it gets no control. This is where
            that refusal belongs — fix round 1 moved it here out of `preconditionOf`,
            which is the hook's channel for a **missing token** and made the panel print
            "this read carried no precondition" beside a row whose read carried the
            version perfectly well. Absent, not disabled, and for the true reason.

            Every rule key keeps exactly one non-retired row (a patch retires the
            incumbent as it inserts the replacement), so hiding these leaves one entry
            point per key rather than none.
          */
          row.fields.status === 'retired' ? null : mode.kind === 'edit' ? (
            <button
              type="button"
              className="wp-button"
              data-testid={`u16-select-${row.id}`}
              onClick={() => {
                setSelectedId(row.id);
                state.resetDraft();
              }}
            >
              {t('alertRules.row.select')}
            </button>
          ) : (
            <button
              type="button"
              className="wp-button"
              data-testid={`u16-edit-${row.id}`}
              onClick={() => {
                const rule = state.versions.find((one) => one.id === row.id);
                if (rule === undefined) return;
                setMode({ kind: 'edit', ruleKey: rule.rule_key });
                setSelectedId(rule.id);
                state.resetDraft();
              }}
            >
              {t('alertRules.row.edit')}
            </button>
          )
        }
      />

      {/*
        What the lifecycle does NOT have, said rather than implied — the sentence U15's
        review made that screen add, and here it is about the whole endpoint rather than
        one flag: alert rules have no activate call at all, so putting one into force is
        a status change on the row and it travels through the same Save.
      */}
      <p className="wp-small wp-muted" data-testid="u16-activate-note">
        {t('alertRules.activate.note')}
      </p>

      {/*
        Fix round 1. The panel's diff is always headed "against the version in force",
        and when a lineage has none — a rule that has only ever been a draft, or one
        switched off — `activeRow` is `null` and every touched key renders through
        `config.diff.absent` as "the version in force has no such field". That is the
        hook saying "no baseline" in words written for a missing field. The screen says
        the true thing itself rather than letting that stand for it.
      */}
      {mode.kind === 'edit' && state.active === null ? (
        <p className="wp-small wp-muted" data-testid="u16-no-active">
          {t('alertRules.noActive')}
        </p>
      ) : null}

      {mode.kind === 'create' ? (
        <section
          className="wp-section wp-c-form"
          aria-labelledby="u16-new-heading"
          data-testid="u16-new"
        >
          <h2 className="wp-section__title" id="u16-new-heading">
            {t('alertRules.create.title')}
          </h2>

          <div className="wp-field">
            <label className="wp-field__label" htmlFor="u16-new-name">
              {t('alertRules.field.name')}
            </label>
            <input
              className="wp-field__input"
              id="u16-new-name"
              data-testid="u16-new-name"
              type="text"
              maxLength={120}
              value={newRule.name}
              onChange={(event) => {
                setNewRule({ ...newRule, name: event.target.value });
              }}
            />
            <p className="wp-small wp-muted">{t('alertRules.create.keyHint')}</p>
          </div>

          <div className="wp-field">
            <label className="wp-field__label" htmlFor="u16-new-trigger">
              {t('alertRules.field.trigger')}
            </label>
            <select
              className="wp-field__input"
              id="u16-new-trigger"
              data-testid="u16-new-trigger"
              value={newRule.trigger}
              onChange={(event) => {
                const trigger = event.target.value as AlertTrigger;
                // A different trigger publishes different fields, so conditions
                // written against the old one are dropped rather than carried into a
                // rule where every one of them would read `field_absent`.
                setNewRule({ ...newRule, trigger, conditions: [] });
              }}
            >
              {ALERT_TRIGGERS.map((trigger) => (
                <option key={trigger} value={trigger}>
                  {trigger}
                </option>
              ))}
            </select>
            {newRuleFields.length === 0 ? (
              <p className="wp-small wp-muted" data-testid="u16-new-nosource">
                {t('alertRules.trigger.noFactSource')}
              </p>
            ) : null}
          </div>

          <ConditionEditor
            conditions={newRule.conditions}
            fields={newRuleFields}
            onChange={(conditions) => {
              setNewRule({ ...newRule, conditions });
            }}
            testId="u16-new-conditions"
          />

          <div className="wp-field">
            <label className="wp-field__label" htmlFor="u16-new-severity">
              {t('alertRules.field.severity')}
            </label>
            <select
              className="wp-field__input"
              id="u16-new-severity"
              data-testid="u16-new-severity"
              value={newRule.severity}
              onChange={(event) => {
                setNewRule({ ...newRule, severity: event.target.value as AlertSeverity });
              }}
            >
              {ALERT_SEVERITIES.map((severity) => (
                <option key={severity} value={severity}>
                  {t(SEVERITY_LABEL[severity])}
                </option>
              ))}
            </select>
          </div>

          <div className="wp-field">
            <label className="wp-field__label" htmlFor="u16-new-window">
              {t('alertRules.field.window')}
            </label>
            <input
              className="wp-field__input"
              id="u16-new-window"
              data-testid="u16-new-window"
              type="number"
              min={1}
              max={527040}
              value={newRule.window_minutes}
              onChange={(event) => {
                setNewRule({ ...newRule, window_minutes: Number(event.target.value) });
              }}
            />
          </div>

          <div className="wp-field">
            <label className="wp-field__label" htmlFor="u16-new-dedup">
              {t('alertRules.field.dedup')}
            </label>
            <input
              className="wp-field__input"
              id="u16-new-dedup"
              data-testid="u16-new-dedup"
              type="number"
              min={0}
              max={527040}
              value={newRule.dedup_window_minutes}
              onChange={(event) => {
                setNewRule({ ...newRule, dedup_window_minutes: Number(event.target.value) });
              }}
            />
          </div>

          <div className="wp-field">
            <label className="wp-field__label" htmlFor="u16-new-cooldown">
              {t('alertRules.field.cooldown')}
            </label>
            <input
              className="wp-field__input"
              id="u16-new-cooldown"
              data-testid="u16-new-cooldown"
              type="number"
              min={0}
              max={527040}
              value={newRule.cooldown_minutes}
              onChange={(event) => {
                setNewRule({ ...newRule, cooldown_minutes: Number(event.target.value) });
              }}
            />
          </div>

          <fieldset className="wp-c-bar__group" data-testid="u16-new-groupby">
            <legend>{t('alertRules.field.groupBy')}</legend>
            {ALERT_GROUP_BY_FIELDS.map((groupBy) => (
              <label className="wp-toggle" key={groupBy} htmlFor={`u16-new-groupby-${groupBy}`}>
                <input
                  id={`u16-new-groupby-${groupBy}`}
                  type="checkbox"
                  data-testid={`u16-new-groupby-${groupBy}`}
                  checked={newRule.group_by.includes(groupBy)}
                  onChange={(event) => {
                    setNewRule({
                      ...newRule,
                      group_by: event.target.checked
                        ? [...newRule.group_by, groupBy]
                        : newRule.group_by.filter((one) => one !== groupBy),
                    });
                  }}
                />
                <span>{groupBy}</span>
              </label>
            ))}
          </fieldset>

          <ActionEditor
            actions={newRule.actions}
            onChange={(actions) => {
              setNewRule({ ...newRule, actions });
            }}
            testId="u16-new-actions"
          />

          <div className="wp-field">
            <label className="wp-field__label" htmlFor="u16-new-status">
              {t('alertRules.field.status')}
            </label>
            <select
              className="wp-field__input"
              id="u16-new-status"
              data-testid="u16-new-status"
              value={newRule.status}
              onChange={(event) => {
                setNewRule({ ...newRule, status: event.target.value as CreatableStatus });
              }}
            >
              {CREATABLE_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            <p className="wp-small wp-muted">{t('alertRules.create.statusHint')}</p>
          </div>

          {/* Absent, not disabled. With no name, no condition or no action there is no
              request to make: the server would answer `422` naming the field. */}
          {canCreate ? (
            <button
              type="button"
              className="wp-button wp-button--primary"
              data-testid="u16-new-submit"
              onClick={() => {
                state.create();
              }}
            >
              {t('alertRules.create.submit')}
            </button>
          ) : (
            <p className="wp-small wp-muted" data-testid="u16-new-blocked">
              {t('alertRules.create.blocked')}
            </p>
          )}
        </section>
      ) : null}

      {target === null ? null : (
        <section
          className="wp-section wp-c-form"
          aria-labelledby="u16-form-heading"
          data-testid="u16-form"
          key={target.id}
        >
          <h2
            className="wp-section__title"
            id="u16-form-heading"
            data-testid="u16-form-heading"
            /* Which version the form is really pointed at, free of any wording: the
               heading's own sentence carries the name, and a name can contain a digit
               that reads like a version number. */
            data-version={String(target.version)}
          >
            {t('alertRules.form.title', {
              name: target.name,
              version: String(target.version),
            })}
          </h2>
          <p className="wp-small wp-muted" data-testid="u16-form-trigger">
            {t('alertRules.form.trigger', { trigger: target.trigger, status: target.status })}
          </p>

          {target.status === 'retired' ? (
            /* A retired version is history: the server refuses a patch on it with
               `WORKFORCE_INVALID_STATE_TRANSITION`. No form, rather than a form whose
               Save could not succeed. */
            <p className="wp-small wp-muted" data-testid="u16-form-retired">
              {t('alertRules.form.retired')}
            </p>
          ) : (
            <>
              <div className="wp-field">
                <label className="wp-field__label" htmlFor="u16-name">
                  {t('alertRules.field.name')}
                </label>
                <input
                  className="wp-field__input"
                  id="u16-name"
                  data-testid="u16-name"
                  type="text"
                  maxLength={120}
                  value={shown('name', target.name)}
                  onChange={(event) => {
                    state.setField('name', event.target.value);
                  }}
                />
              </div>

              <ConditionEditor
                conditions={shown('conditions', target.conditions)}
                fields={ALERT_TRIGGER_FIELDS[target.trigger]}
                onChange={(conditions) => {
                  state.setField('conditions', conditions);
                }}
                testId="u16-conditions"
              />

              <div className="wp-field">
                <label className="wp-field__label" htmlFor="u16-severity">
                  {t('alertRules.field.severity')}
                </label>
                <select
                  className="wp-field__input"
                  id="u16-severity"
                  data-testid="u16-severity"
                  value={shown('severity', target.severity)}
                  onChange={(event) => {
                    state.setField('severity', event.target.value as AlertSeverity);
                  }}
                >
                  {ALERT_SEVERITIES.map((severity) => (
                    <option key={severity} value={severity}>
                      {t(SEVERITY_LABEL[severity])}
                    </option>
                  ))}
                </select>
              </div>

              <div className="wp-field">
                <label className="wp-field__label" htmlFor="u16-window">
                  {t('alertRules.field.window')}
                </label>
                <input
                  className="wp-field__input"
                  id="u16-window"
                  data-testid="u16-window"
                  type="number"
                  min={1}
                  max={527040}
                  value={shown('window_minutes', target.window_minutes ?? 60)}
                  onChange={(event) => {
                    state.setField('window_minutes', Number(event.target.value));
                  }}
                />
              </div>

              <div className="wp-field">
                <label className="wp-field__label" htmlFor="u16-cooldown">
                  {t('alertRules.field.cooldown')}
                </label>
                <input
                  className="wp-field__input"
                  id="u16-cooldown"
                  data-testid="u16-cooldown"
                  type="number"
                  min={0}
                  max={527040}
                  value={shown('cooldown_minutes', target.cooldown_minutes)}
                  onChange={(event) => {
                    state.setField('cooldown_minutes', Number(event.target.value));
                  }}
                />
              </div>

              <div className="wp-field">
                <label className="wp-field__label" htmlFor="u16-dedup">
                  {t('alertRules.field.dedup')}
                </label>
                <input
                  className="wp-field__input"
                  id="u16-dedup"
                  data-testid="u16-dedup"
                  type="number"
                  min={0}
                  max={527040}
                  value={shown('dedup_window_minutes', target.dedup_window_minutes)}
                  onChange={(event) => {
                    state.setField('dedup_window_minutes', Number(event.target.value));
                  }}
                />
              </div>

              <fieldset className="wp-c-bar__group" data-testid="u16-groupby">
                <legend>{t('alertRules.field.groupBy')}</legend>
                {ALERT_GROUP_BY_FIELDS.map((groupBy) => {
                  const current = shown('group_by', target.group_by);
                  return (
                    <label className="wp-toggle" key={groupBy} htmlFor={`u16-groupby-${groupBy}`}>
                      <input
                        id={`u16-groupby-${groupBy}`}
                        type="checkbox"
                        data-testid={`u16-groupby-${groupBy}`}
                        checked={current.includes(groupBy)}
                        onChange={(event) => {
                          state.setField(
                            'group_by',
                            event.target.checked
                              ? [...current, groupBy]
                              : current.filter((one) => one !== groupBy),
                          );
                        }}
                      />
                      <span>{groupBy}</span>
                    </label>
                  );
                })}
              </fieldset>

              <ActionEditor
                actions={shown('actions', target.actions)}
                onChange={(actions) => {
                  state.setField('actions', actions);
                }}
                testId="u16-actions"
              />

              {/*
                The status move — the whole of "activation" on this backend. Picking a
                value here and pressing Save sends `PATCH {"status":"active"}`, which is
                a status-only patch and therefore moves the row rather than minting a
                version. The current status is offered too, so choosing it touches
                nothing: the server refuses a move to the status a row already holds.
              */}
              <div className="wp-field">
                <label className="wp-field__label" htmlFor="u16-status">
                  {t('alertRules.field.status')}
                </label>
                <select
                  className="wp-field__input"
                  id="u16-status"
                  data-testid="u16-status"
                  value={shown('status', target.status as PatchableStatus)}
                  onChange={(event) => {
                    state.setField('status', event.target.value as PatchableStatus);
                  }}
                >
                  {/*
                    Fix round 1: `disabled`, so the current value still shows and cannot
                    be re-selected. `AlertRulePatchRequest`'s status is
                    `enum: [active, disabled]` — `draft` is unreachable by a patch — and
                    `setField` does not un-touch a key restored to its original value,
                    so picking `active` and then `draft` again armed Save with
                    `{"status":"draft"}` and earned a `422` naming a field the person
                    had done nothing wrong in.
                  */}
                  {target.status === 'draft' ? (
                    <option value="draft" disabled>
                      draft
                    </option>
                  ) : null}
                  {PATCHABLE_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
                <p className="wp-small wp-muted">{t('alertRules.status.hint')}</p>
              </div>

              <p className="wp-small wp-muted" data-testid="u16-no-trigger-edit">
                {t('alertRules.form.noTriggerEdit')}
              </p>

              <div className="wp-c-actions">
                {state.touched.length > 0 && state.canWrite(target) ? (
                  <button
                    type="button"
                    className="wp-button wp-button--primary"
                    data-testid="u16-save"
                    onClick={() => {
                      state.patch(target);
                    }}
                  >
                    {t('alertRules.save')}
                  </button>
                ) : (
                  <p className="wp-small wp-muted" data-testid="u16-save-absent">
                    {state.touched.length === 0
                      ? t('alertRules.save.nothing')
                      : t('alertRules.save.blocked')}
                  </p>
                )}
                {state.touched.length === 0 ? null : (
                  <button
                    type="button"
                    className="wp-button"
                    data-testid="u16-discard"
                    onClick={() => {
                      state.resetDraft();
                    }}
                  >
                    {t('alertRules.discard')}
                  </button>
                )}
              </div>
            </>
          )}
        </section>
      )}

      {/* One rule's conditions in words, so the table above is readable without
          reading JSON. Rendered for the version the form points at. */}
      {target === null ? null : (
        <p className="wp-small wp-muted" data-testid="u16-conditions-summary">
          {describeConditions(shown('conditions', target.conditions))}
        </p>
      )}
    </div>
  );
}
